/**
 * useVisiblePolling — pause setInterval when the tab is hidden.
 *
 * Why: barmen often have multiple tabs open (Tables, Bills, Reports, Settings).
 * Without this hook, every tab keeps hitting the backend every 3-30 seconds in
 * the background, which:
 *   - wastes bandwidth on mobile data
 *   - drains battery on tablets
 *   - keeps backend cache always warming and never letting it idle
 *   - delays the FOREGROUND tab's requests when the browser throttles fetch
 *
 * Usage:
 *   useVisiblePolling(() => fetchData(), 5000, [token, clubId]);
 *
 * Behavior:
 *   - Fires the callback once immediately when the tab is visible.
 *   - Schedules setInterval(callback, intervalMs) while the tab is visible.
 *   - On `visibilitychange` → hidden: clears the interval.
 *   - On `visibilitychange` → visible: fires callback (catch up missed ticks)
 *     and restarts the interval.
 *   - Cleans up on unmount.
 *
 * Returns a no-arg `forceRefresh()` you can call to fire the callback now
 * (e.g. from a manual refresh button) without resetting the timer.
 */
import { useEffect, useRef, useCallback } from 'react';

export function useVisiblePolling(callback, intervalMs, deps = []) {
  // Keep the latest callback in a ref so the interval body always calls
  // the freshest closure without resetting the timer on every render.
  const cbRef = useRef(callback);
  const intervalRef = useRef(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { cbRef.current = callback; });

  const forceRefresh = useCallback(() => {
    try { cbRef.current && cbRef.current(); } catch { /* swallow — user-triggered */ }
  }, []);

  useEffect(() => {
    if (!intervalMs || intervalMs <= 0) return undefined;

    const start = () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      // Fire once on mount/visible so the UI doesn't sit empty until the
      // first interval tick.
      try { cbRef.current && cbRef.current(); } catch { /* ignore initial */ }
      intervalRef.current = setInterval(() => {
        try { cbRef.current && cbRef.current(); } catch { /* swallow per tick */ }
      }, intervalMs);
    };

    const stop = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const onVis = () => {
      if (document.hidden) stop();
      else start();
    };

    // Initial state.
    if (document.hidden) {
      // Tab opened in background — wait until it becomes visible.
    } else {
      start();
    }
    document.addEventListener('visibilitychange', onVis);

    return () => {
      document.removeEventListener('visibilitychange', onVis);
      stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalMs, ...deps]);

  return forceRefresh;
}

export default useVisiblePolling;
